import React, { useEffect, useMemo, useState } from 'react';
import { Check, Plus, Pencil, Trash2 } from 'lucide-react';
import { Button } from '../../../../../../../../components/ui/button';
import { Card, CardContent } from '../../../../../../../../components/ui/card';
import { cn } from '../../../../../../../../lib/utils';
import { formatLabel, StatusBadge } from '../../pipeMillReportUtils';
import {
  AdjudicationPanel,
  BendDispositionPanel,
  ChemicalFormulaRow,
  ChemistryResultRow,
  FlatteningDispositionPanel,
  HydrostaticDispositionPanel,
  ItemActionButtons,
  KeyValueRows,
  MiniStatusDot,
  PipeLocationDiagram,
  ReportMetadataRail,
  ResultRow,
  SectionHead,
  StatCard,
  TensileResultRow,
} from './shared/components';
import {
  AUT_DETAIL,
  BEND_DETAIL,
  CHEMICAL_DETAIL,
  CVN_DETAIL,
  DIMENSIONAL_DETAIL,
  HARDNESS_DETAIL,
  HYDROSTATIC_DETAIL,
  STANDARD_REPORT_DETAILS,
} from './shared/sampleData';
import {
  average,
  formatDisplayDate,
  getStandardReportSummaryStats,
  standardLimitText,
  standardScaleMax,
  standardScaleMin,
  statusNoteClassName,
} from './shared/format';
import {
  createSampleMeasurement,
  isSampleDataDeleted,
} from './shared/measurements';
import {
  getProjectLimitField,
  getProjectLimitMax,
  getProjectLimitMin,
  getProjectLimitRows,
  getProjectLimitText,
} from './shared/limits';
import type {
  ChemicalElementDisplay,
  FullReportType,
  ProjectLimitFieldOption,
  ReportDefinition,
  ReportMeasurementItem,
  ReportMetadataEntry,
  ReportSummary,
  StandardCheck,
} from './shared/types';
import type { BaseReportDetailProps } from './shared/reportProps';

export const DimensionalReportDetail: React.FC<{
  heatNumber: string;
  pipeLengthM?: number;
  projectLimitFields?: ProjectLimitFieldOption[];
  frequency?: string;
  measurements?: ReportMeasurementItem[];
  deletedMeasurementIds?: string[];
  onAddMeasurement?: (reportType: FullReportType) => void;
  onEditMeasurement?: (measurement: ReportMeasurementItem) => void;
  onDeleteMeasurement?: (measurement: ReportMeasurementItem) => void;
}> = ({ heatNumber, pipeLengthM = 12.18, projectLimitFields = [], frequency, measurements = [], deletedMeasurementIds = [], onAddMeasurement, onEditMeasurement, onDeleteMeasurement }) => {
  const [activeInspectionId, setActiveInspectionId] = useState<string | null>(null);
  const dimensionalMeasurements = useMemo(
    () => measurements.filter((measurement) => measurement.reportType === 'DIMENSIONAL'),
    [measurements]
  );
  const dimensionalMeasurement = dimensionalMeasurements.find((measurement) => measurement.id === activeInspectionId) || dimensionalMeasurements[0];
  useEffect(() => {
    if (!dimensionalMeasurements.length) {
      if (activeInspectionId) setActiveInspectionId(null);
      return;
    }
    if (!activeInspectionId || !dimensionalMeasurements.some((measurement) => measurement.id === activeInspectionId)) {
      setActiveInspectionId(dimensionalMeasurements[0].id);
    }
  }, [activeInspectionId, dimensionalMeasurements]);
  const odAvg = Number(dimensionalMeasurement?.dimOdAvgMm || 660.7);
  const odMin = Number(dimensionalMeasurement?.dimOdMinMm || 658.9);
  const odMax = Number(dimensionalMeasurement?.dimOdMaxMm || 662.1);
  const ovality = Number(dimensionalMeasurement?.dimOvalityPct || 0.48);
  const wallReadings = [
    Number(dimensionalMeasurement?.dimWtEnd1N || 22.1),
    Number(dimensionalMeasurement?.dimWtEnd1E || 22.4),
    Number(dimensionalMeasurement?.dimWtEnd1S || 22.3),
    Number(dimensionalMeasurement?.dimWtEnd1W || 22.5),
    Number(dimensionalMeasurement?.dimWtEnd2N || 22.2),
    Number(dimensionalMeasurement?.dimWtEnd2E || 22.1),
    Number(dimensionalMeasurement?.dimWtEnd2S || 22.3),
    Number(dimensionalMeasurement?.dimWtEnd2W || 22.4),
  ];
  const lengthM = Number(dimensionalMeasurement?.dimLengthM || pipeLengthM);
  const straightnessMm = Number(dimensionalMeasurement?.dimStraightnessMm || 14.2);
  const bevelAngleDeg = Number(dimensionalMeasurement?.dimBevelAngleDeg || 30.2);
  const endSquarenessMm = Number(dimensionalMeasurement?.dimEndSquarenessMm || 0.8);
  const odSpec = 660.4;
  const wallSpecified = 22.2;
  const getLimit = (fieldPath: string, fallbackMin: number | null, fallbackMax: number | null, fallbackUnit: string, fallbackText: string) => {
    const field = projectLimitFields.find((option) => option.fieldPath === fieldPath);
    return {
      min: field?.min ?? fallbackMin,
      max: field?.max ?? fallbackMax,
      unit: field?.unit || fallbackUnit,
      text: field?.limit || fallbackText,
    };
  };
  const odLimit = getLimit('outer_diameter', 657.1, 663.7, 'mm', '657.1 - 663.7 mm');
  const wallLimit = getLimit('wall_thickness', 19.43, 25.53, 'mm', '19.43 - 25.53 mm');
  const lengthLimit = getLimit('length_m', 12, 12.5, 'm', '12.0 - 12.5 m');
  const ovalityLimit = getLimit('ovality_pct', null, 1.5, '%', '<= 1.5 %');
  const straightnessLimit = getLimit('straightness_mm_m', null, 24.36, 'mm', '<= 24.36 mm');
  const bevelLimit = getLimit('bevel_angle_deg', 25, 35, 'deg', '25 - 35 deg');
  const squarenessLimit = getLimit('end_squareness_mm', null, 1.6, 'mm', '<= 1.6 mm');
  const projectLimitRows: Array<[string, React.ReactNode, boolean?]> = projectLimitFields.length
    ? projectLimitFields.map((field) => [field.label, field.limit, true])
    : [
      ['OD', odLimit.text, true],
      ['WT', wallLimit.text, true],
      ['Length', lengthLimit.text, true],
      ['Ovality', ovalityLimit.text, true],
      ['Straightness', straightnessLimit.text, true],
    ];
  const minWall = Math.min(...wallReadings);
  const maxWall = Math.max(...wallReadings);
  const isWithinLimit = (value: number, limit: { min: number | null; max: number | null }) => {
    if (limit.min != null && value < limit.min) return false;
    if (limit.max != null && value > limit.max) return false;
    return true;
  };
  const odAvgStatus = isWithinLimit(odAvg, odLimit) ? 'PASS' : 'FAIL';
  const odStatus = [odAvg, odMin, odMax].every((value) => isWithinLimit(value, odLimit)) ? 'PASS' : 'FAIL';
  const wallStatus = wallReadings.every((value) => isWithinLimit(value, wallLimit)) ? 'PASS' : 'FAIL';
  const lengthStatus = isWithinLimit(lengthM, lengthLimit) ? 'PASS' : 'FAIL';
  const ovalityStatus = isWithinLimit(ovality, ovalityLimit) ? 'PASS' : 'FAIL';
  const straightnessStatus = isWithinLimit(straightnessMm, straightnessLimit) ? 'PASS' : 'FAIL';
  const bevelStatus = isWithinLimit(bevelAngleDeg, bevelLimit) ? 'PASS' : 'FAIL';
  const squarenessStatus = isWithinLimit(endSquarenessMm, squarenessLimit) ? 'PASS' : 'FAIL';
  const status = [odStatus, wallStatus, lengthStatus, ovalityStatus, straightnessStatus, bevelStatus, squarenessStatus].includes('FAIL') ? 'FAIL' : 'PASS';
  const pos = (value: number, min: number, max: number) => max === min ? 50 : Math.max(0, Math.min(100, ((value - min) / (max - min)) * 100));
  const odLo = odLimit.min ?? Math.min(odMin, odAvg, odSpec);
  const odHi = odLimit.max ?? Math.max(odMax, odAvg, odSpec);
  const wallMin = wallLimit.min ?? Math.min(...wallReadings);
  const wallMax = wallLimit.max ?? Math.max(...wallReadings);
  return (
    <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_340px]">
      <main className="space-y-5 p-6">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h3 className="font-heading text-[16px] font-semibold text-blueprint-blue">Outside diameter</h3>
            <div className="mt-1 text-[12px] text-heritage-grey">Average of all OD readings - project limit {odLimit.text}</div>
          </div>
          <Button type="button" size="sm" onClick={() => onAddMeasurement?.('DIMENSIONAL')} className="bg-hero-red text-white hover:bg-hero-red-60">
            <Plus className="mr-2 h-4 w-4" />
            Add Inspection
          </Button>
        </div>

        <div className="grid grid-cols-1 gap-4 xl:grid-cols-[minmax(0,2fr)_minmax(260px,1fr)]">
          <Card className="border-[#E2E5EA]">
            <CardContent className="p-5">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <div className="text-[10px] font-bold uppercase tracking-wider text-heritage-grey">OD - Average</div>
                  <div className="mt-1 flex items-baseline gap-1">
                    <span className="font-heading text-[30px] font-semibold text-blueprint-blue">{odAvg.toFixed(1)}</span>
                    <span className="text-[12px] text-text-grey">mm</span>
                  </div>
                  <div className="font-mono text-[10px] text-heritage-grey">project limit {odLimit.text}</div>
                </div>
                <StatusBadge status={odAvgStatus} />
              </div>
              <div className="relative mt-6 h-6">
                <div className="absolute left-0 right-0 top-2 h-1 rounded bg-[#E8F3EF]" />
                <div className="absolute top-0 h-6 w-px bg-text-grey" style={{ left: `${pos(odMin, odLo, odHi)}%` }} />
                <div className="absolute top-0 h-6 w-px bg-text-grey" style={{ left: `${pos(odMax, odLo, odHi)}%` }} />
                <div className="absolute top-3 h-0.5 bg-text-grey" style={{ left: `${pos(odMin, odLo, odHi)}%`, width: `${Math.max(1, pos(odMax, odLo, odHi) - pos(odMin, odLo, odHi))}%` }} />
                <div className={cn('absolute -top-0.5 h-5 w-3 rounded-sm border-2 border-white', odAvgStatus === 'PASS' ? 'bg-galvanizing-green' : 'bg-readable-red')} style={{ left: `calc(${pos(odAvg, odLo, odHi)}% - 6px)` }} />
                <div className="absolute -top-1 h-7 w-px bg-blueprint-blue" style={{ left: `${pos(odSpec, odLo, odHi)}%` }} />
              </div>
              <div className="mt-2 flex justify-between font-mono text-[10px] text-heritage-grey">
                <span>{odLo.toFixed(1)}</span>
                <span>min {odMin.toFixed(1)}</span>
                <span>spec {odSpec.toFixed(1)}</span>
                <span>max {odMax.toFixed(1)}</span>
                <span>{odHi.toFixed(1)}</span>
              </div>
            </CardContent>
          </Card>

          <Card className="border-[#E2E5EA]">
            <CardContent className="p-5">
              <div className="flex items-start justify-between">
                <div>
                  <div className="text-[10px] font-bold uppercase tracking-wider text-heritage-grey">Ovality</div>
                  <div className="mt-1 flex items-baseline gap-1">
                    <span className="font-heading text-[28px] font-semibold text-blueprint-blue">{ovality.toFixed(2)}</span>
                    <span className="text-[12px] text-text-grey">%</span>
                  </div>
                  <div className="font-mono text-[10px] text-heritage-grey">project limit {ovalityLimit.text}</div>
                </div>
                <StatusBadge status={ovalityStatus} />
              </div>
              <div className="relative mt-5 h-3 rounded bg-[#EEF0F3]">
                <div className={cn('absolute bottom-0 left-0 top-0 rounded', ovalityStatus === 'PASS' ? 'bg-[#CDEAD9]' : 'bg-[rgba(196,0,31,0.14)]')} style={{ width: `${Math.min(100, (ovality / (ovalityLimit.max || Math.max(ovality, 1))) * 100)}%` }} />
                <div className={cn('absolute -top-1 h-5 w-px', ovalityStatus === 'PASS' ? 'bg-galvanizing-green' : 'bg-readable-red')} style={{ left: `${Math.min(100, (ovality / (ovalityLimit.max || Math.max(ovality, 1))) * 100)}%` }} />
              </div>
              <div className="mt-2 flex justify-between font-mono text-[10px] text-heritage-grey"><span>0 %</span><span>{ovalityLimit.max ?? ovality} %</span></div>
            </CardContent>
          </Card>
        </div>

        <Card className="border-[#E2E5EA]">
          <CardContent className="p-0">
            <SectionHead title="Wall thickness - 8 measurement points" subtitle="4 points at each pipe end" />
            <div className="p-5">
              <div className="flex flex-wrap items-start gap-6">
                <div><div className="text-[10px] text-heritage-grey">Specified</div><div className="font-heading text-[17px] font-semibold text-blueprint-blue">{wallSpecified.toFixed(1)} mm</div></div>
                <div><div className="text-[10px] text-heritage-grey">Lower limit</div><div className="font-heading text-[17px] font-semibold text-blueprint-blue">{wallMin.toFixed(2)} mm</div></div>
                <div><div className="text-[10px] text-heritage-grey">Upper limit</div><div className="font-heading text-[17px] font-semibold text-blueprint-blue">{wallMax.toFixed(2)} mm</div></div>
                <div><div className="text-[10px] text-heritage-grey">Measured min - max</div><div className={cn('font-heading text-[17px] font-semibold', wallStatus === 'PASS' ? 'text-galvanizing-green' : 'text-readable-red')}>{minWall.toFixed(1)} - {maxWall.toFixed(1)} mm</div></div>
                <div className="ml-auto"><StatusBadge status={wallStatus} /></div>
              </div>
              <div className="mt-5 grid grid-cols-2 gap-3 md:grid-cols-4 xl:grid-cols-8">
                {wallReadings.map((value, index) => (
                  <div key={index} className="text-center">
                    <div className="font-mono text-[10px] text-heritage-grey">{index < 4 ? 'End 1' : 'End 2'} - {['N', 'E', 'S', 'W'][index % 4]}</div>
                    <div className="mt-1 font-heading text-[16px] font-semibold text-blueprint-blue">{value.toFixed(1)}</div>
                    <div className="relative mt-1 h-2 rounded bg-[#EEF0F3]">
                      <div className="absolute bottom-0 top-0 rounded bg-[#CDEAD9]" style={{ left: `${pos(wallMin, wallSpecified * 0.8, wallSpecified * 1.2)}%`, width: `${Math.max(1, pos(wallMax, wallSpecified * 0.8, wallSpecified * 1.2) - pos(wallMin, wallSpecified * 0.8, wallSpecified * 1.2))}%` }} />
                      <div className={cn('absolute -top-1 h-4 w-px', isWithinLimit(value, wallLimit) ? 'bg-galvanizing-green' : 'bg-readable-red')} style={{ left: `${pos(value, wallSpecified * 0.8, wallSpecified * 1.2)}%` }} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="overflow-hidden border-[#E2E5EA]">
          <CardContent className="p-0">
            <SectionHead
              title="Length / Geometry Inspections"
              subtitle="Select a row to update the OD and wall thickness details above"
              right={(
                <Button type="button" size="sm" onClick={() => onAddMeasurement?.('DIMENSIONAL')} className="bg-hero-red text-white hover:bg-hero-red-60">
                  <Plus className="mr-2 h-4 w-4" />
                  Add Item
                </Button>
              )}
            />
            {dimensionalMeasurements.length === 0 ? (
              <div className="p-4 text-[13px] text-heritage-grey">No dimensional inspections saved yet.</div>
            ) : (
              <table className="w-full text-left">
                <thead className="border-b border-[#E2E5EA] bg-page-bg text-[10px] font-bold uppercase tracking-wider text-heritage-grey">
                  <tr>
                    <th className="px-4 py-3">Frequency</th>
                    <th className="px-4 py-3 text-right">Length</th>
                    <th className="px-4 py-3 text-right">Straightness</th>
                    <th className="px-4 py-3 text-right">Bevel angle</th>
                    <th className="px-4 py-3 text-right">End squareness</th>
                    <th className="px-4 py-3">Status</th>
                    <th className="px-4 py-3 text-right">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#EEF0F3] text-[12px] text-text-grey">
                  {dimensionalMeasurements.map((measurement) => (
                    <tr key={measurement.id} onClick={() => setActiveInspectionId(measurement.id)} className={cn('cursor-pointer', dimensionalMeasurement?.id === measurement.id && 'bg-[#E7F3FF]')}>
                      <td className="px-4 py-3 font-mono font-semibold">{measurement.frequency || frequency || 'per_pipe'}</td>
                      <td className="px-4 py-3 text-right font-mono">{measurement.dimLengthM || '-'}</td>
                      <td className="px-4 py-3 text-right font-mono">{measurement.dimStraightnessMm || '-'}</td>
                      <td className="px-4 py-3 text-right font-mono">{measurement.dimBevelAngleDeg || '-'}</td>
                      <td className="px-4 py-3 text-right font-mono">{measurement.dimEndSquarenessMm || '-'}</td>
                      <td className="px-4 py-3"><StatusBadge status={measurement.status} /></td>
                      <td className="px-4 py-3 text-right">
                        <ItemActionButtons measurement={measurement} onEditMeasurement={onEditMeasurement} onDeleteMeasurement={onDeleteMeasurement} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </CardContent>
        </Card>
      </main>

      <ReportMetadataRail
        rows={[
          ['Report ID', DIMENSIONAL_DETAIL.reportId, true],
          ['Performed at', formatDisplayDate(DIMENSIONAL_DETAIL.performedAt), true],
          ['Heat', heatNumber, true],
          ['Station', DIMENSIONAL_DETAIL.station],
          ['Inspector', DIMENSIONAL_DETAIL.inspector],
          ['Specification', DIMENSIONAL_DETAIL.spec],
          ['Source file', DIMENSIONAL_DETAIL.sourceFile, true],
        ]}
        extraTitle="Limits In Effect"
        extraRows={projectLimitRows}
      >
        <div className="mt-5 text-[10px] font-bold uppercase tracking-wider text-heritage-grey">Frequency / Scope</div>
        <Card className="mt-3 border-[#E2E5EA]">
          <CardContent className="p-4">
            <KeyValueRows rows={[
              ['Frequency', dimensionalMeasurement?.frequency || frequency || 'per_pipe', true],
              ['Inspections', `${dimensionalMeasurements.length} saved`, true],
              ['WT points', '8 per pipe (ITP)'],
              ['Selected status', <StatusBadge key="status" status={status} />],
            ]} />
          </CardContent>
        </Card>
        <AdjudicationPanel />
      </ReportMetadataRail>
    </div>
  );
};

const dimensionalLimitText = (check: typeof DIMENSIONAL_DETAIL.checks[number]) => {
  if (check.min != null && check.max != null) return `${check.min}-${check.max} ${check.unit}`;
  if (check.min != null) return `>= ${check.min} ${check.unit}`;
  if (check.max != null) return `<= ${check.max} ${check.unit}`;
  return '-';
};

const dimensionalScaleMin = (check: typeof DIMENSIONAL_DETAIL.checks[number]) => {
  if (check.min != null) return Math.max(0, check.min - Math.abs(check.min * 0.02));
  return 0;
};

const dimensionalScaleMax = (check: typeof DIMENSIONAL_DETAIL.checks[number]) => {
  if (check.max != null) return check.max + Math.max(1, Math.abs(check.max * 0.05));
  return check.value * 1.2;
};
